import React from 'react';
import { Shop } from '../../lib/supabase/api/shopService';
import ShopCard from './ShopCard';
import { checkShopCompatibility } from '../../utils/shopCompatibility';
import { PrintSettings } from '../../types/documentTypes';

interface ShopListProps {
  shops: Shop[];
  isLoading: boolean;
  selectedShopId: string | null;
  setSelectedShop: (shopId: string) => void;
  locationShared: boolean;
  paperType?: string; // Current paper type from print settings
  printSettings?: { [documentId: string]: PrintSettings }; // All user's print settings
}

const ShopList: React.FC<ShopListProps> = ({
  shops,
  isLoading,
  selectedShopId,
  setSelectedShop,
  locationShared,
  paperType,
  printSettings = {}
}) => {
  // Sort shops: compatible first, then incompatible
  const sortedShops = React.useMemo(() => {
    return [...shops].sort((a, b) => {
      const aCompat = checkShopCompatibility(a, printSettings);
      const bCompat = checkShopCompatibility(b, printSettings);
      
      // Compatible shops first
      if (aCompat.isCompatible && !bCompat.isCompatible) return -1;
      if (!aCompat.isCompatible && bCompat.isCompatible) return 1;
      
      // Within each group, sort by distance if available
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      
      return 0;
    });
  }, [shops, printSettings]);

  return (
    <>
      {/* Loading indicator */}
      {isLoading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">
            {locationShared ? 'Finding nearby print shops...' : 'Loading print shops...'}
          </p>
        </div>
      )}
      
      {/* Shops list */}
      <div className="max-h-96 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
        {!isLoading && shops.length === 0 && (
          <p className="text-center text-gray-500 py-4">No print shops found. Try a different search term.</p>
        )}
        
        {!isLoading && sortedShops.map(shop => {
          const compatibility = checkShopCompatibility(shop, printSettings);
          
          return (
            <ShopCard
              key={shop.id}
              shop={shop}
              isSelected={selectedShopId === shop.id}
              onSelect={setSelectedShop}
              paperType={paperType}
              isCompatible={compatibility.isCompatible}
              incompatibilityReasons={compatibility.reasons}
            />
          );
        })}
      </div>
    </>
  );
};

export default ShopList;
